/**
 * World-Class Real-Time Phone Number Validation
 * Uses libphonenumber-js for country-specific validation
 * Provides detailed feedback with format requirements
 */

import { parsePhoneNumber, isValidPhoneNumber, isPossiblePhoneNumber, getCountries, getCountryCallingCode, AsYouType } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

/**
 * Real-time phone validation with country-specific feedback
 * @param {string} phoneNumber - Phone number in any format
 * @param {string} countryCode - ISO country code (e.g., 'IN', 'US', 'GB')
 * @returns {Object} - Validation result with detailed feedback
 */
export const validatePhoneRealTime = (phoneNumber, countryCode = null) => {
  // Empty state
  if (!phoneNumber || !phoneNumber.trim()) {
    return {
      isValid: false,
      isValidating: false,
      message: 'Phone number is required',
      formatHint: 'Enter phone number with country code (e.g., +91 9876543210)',
      digitsCount: 0,
      requiredDigits: null,
      country: null
    };
  }

  const trimmed = phoneNumber.trim();
  
  // Check if it starts with + (international format)
  if (!trimmed.startsWith('+')) {
    return {
      isValid: false,
      isValidating: true,
      message: 'Phone number must include country code',
      formatHint: 'Start with + followed by country code (e.g., +91 for India, +1 for US)',
      digitsCount: trimmed.replace(/\D/g, '').length,
      requiredDigits: null,
      country: null
    };
  }

  try {
    // Try to parse the phone number
    let parsedNumber;
    let detectedCountry = null;
    
    if (countryCode) {
      // If country is explicitly selected, use it
      try {
        parsedNumber = parsePhoneNumber(trimmed, countryCode.toUpperCase());
        detectedCountry = countryCode.toUpperCase();
      } catch (e) {
        // If parsing with specific country fails, try without
        parsedNumber = parsePhoneNumber(trimmed);
        detectedCountry = parsedNumber?.country || null;
      }
    } else {
      // Auto-detect country from number
      parsedNumber = parsePhoneNumber(trimmed);
      detectedCountry = parsedNumber?.country || null;
    }

    if (!parsedNumber) {
      return {
        isValid: false,
        isValidating: true,
        message: '',
        formatHint: '',
        digitsCount: trimmed.replace(/\D/g, '').length,
        requiredDigits: null,
        country: detectedCountry
      };
    }

    const callingCode = parsedNumber.countryCallingCode;
    const nationalNumber = parsedNumber.nationalNumber;
    
    // Check if number is valid (strict) and possible (lenient)
    const isValid = isValidPhoneNumber(trimmed, detectedCountry);
    const isPossible = isPossiblePhoneNumber(trimmed, detectedCountry);
    
    // Get format requirements for the country using libphonenumber-js
    let formatHint = '';
    let requiredDigits = null;
    let numberType = null;
    
    // Try to get example number for the country (works for ALL countries automatically)
    let exampleNumber = null;
    let exampleNational = null;
    let examplePrefix = null;
    
    try {
      if (detectedCountry && examples[detectedCountry]) {
        exampleNumber = parsePhoneNumber(examples[detectedCountry], detectedCountry);
        if (exampleNumber) {
          exampleNational = exampleNumber.nationalNumber;
          // Get prefix (first 2-3 digits) for format guidance
          if (exampleNational) {
            examplePrefix = exampleNational.substring(0, Math.min(3, exampleNational.length));
          }
          formatHint = `Example: ${exampleNumber.formatInternational()}`;
          // Get the national number length from example
          if (exampleNational) {
            requiredDigits = exampleNational.length;
          }
        }
      }
    } catch (e) {
      // If example not available, use generic format
      formatHint = `Format: +${callingCode} [number]`;
    }
    
    // Note: numberType variable is available for future use if needed
    // Currently not using getNumberType to avoid import issues
    
    // Get expected digit length from example number (works for ALL countries automatically)
    if (nationalNumber && !requiredDigits) {
      // Try to get from example first (most accurate - works for all countries)
      try {
        if (detectedCountry && examples[detectedCountry]) {
          const exampleNumber = parsePhoneNumber(examples[detectedCountry], detectedCountry);
          if (exampleNumber && exampleNumber.nationalNumber) {
            requiredDigits = exampleNumber.nationalNumber.length;
          }
        }
      } catch (e) {
        // If example fetch fails, continue with fallback
      }
      
      // Fallback: Use typical lengths for common countries if example not available
      // This is only a fallback - examples should work for most countries
      if (!requiredDigits) {
        const typicalLengths = {
          'US': 10, 'CA': 10, 'IN': 10, 'GB': 10, 'AU': 9, 'DE': 11, 'FR': 9,
          'IT': 9, 'ES': 9, 'BR': 10, 'MX': 10, 'JP': 10, 'CN': 11, 'KR': 10,
          'AE': 9, 'SA': 9, 'SG': 8, 'MY': 9, 'TH': 9, 'ID': 9, 'PH': 10,
          'VN': 9, 'NZ': 8, 'ZA': 9, 'PK': 10, 'BD': 10, 'RU': 10, 'TR': 10
        };
        
        if (detectedCountry && typicalLengths[detectedCountry]) {
          requiredDigits = typicalLengths[detectedCountry];
        } else {
          // Generic: Use current length as reference if within valid range
          requiredDigits = nationalNumber.length >= 7 && nationalNumber.length <= 15 ? nationalNumber.length : null;
        }
      }
    }

    // Get country name
    const countryName = detectedCountry ? getCountryName(detectedCountry) : null;

    // Get detailed validation feedback using libphonenumber-js capabilities
    // This works for ALL countries automatically
    let formatError = null;
    let detailedHint = '';
    
    if (!isValid && detectedCountry && nationalNumber) {
      // Check if number is possible (has correct length/format structure)
      const isPossible = isPossiblePhoneNumber(trimmed, detectedCountry);
      
      if (!isPossible) {
        // Number structure is completely wrong
        formatError = `Invalid number format for ${countryName || 'this country'}`;
        // Always try to show example if available
        if (exampleNumber && exampleNational) {
          detailedHint = `Example: ${exampleNumber.formatInternational()}`;
          if (examplePrefix) {
            detailedHint += ` (typically starts with ${examplePrefix})`;
          }
        } else {
          detailedHint = formatHint || `Please check the format. Example: +${callingCode} [number]`;
        }
      } else {
        // Number structure is possible but not valid - likely prefix/format issue
        // Show example with prefix information for ALL countries
        if (exampleNumber && exampleNational && examplePrefix) {
          const formattedExample = exampleNumber.formatInternational();
          
          // Check if user's prefix matches example prefix
          const userPrefix = nationalNumber.length >= examplePrefix.length 
            ? nationalNumber.substring(0, examplePrefix.length)
            : nationalNumber.substring(0, Math.min(3, nationalNumber.length));
          
          if (userPrefix !== examplePrefix && nationalNumber.length >= 2) {
            // User's prefix doesn't match - show specific guidance
            formatError = `Invalid format for ${countryName || 'this country'}. Numbers typically start with ${examplePrefix}`;
            detailedHint = `Example: ${formattedExample} (starts with ${examplePrefix}${exampleNational.length > examplePrefix.length ? '...' : ''})`;
          } else {
            // Prefix matches but still invalid - show format example
            formatError = `Invalid ${countryName || 'phone number'} format`;
            detailedHint = `Example: ${formattedExample}`;
            if (examplePrefix) {
              detailedHint += ` (starts with ${examplePrefix})`;
            }
          }
        } else {
          // Fallback if example not available
          formatError = `Invalid ${countryName || 'phone number'} format`;
          detailedHint = formatHint || `Please verify the number format for ${countryName || 'this country'}`;
        }
      }
    }

    if (isValid) {
      return {
        isValid: true,
        isValidating: false,
        message: '',
        formatHint: formatHint || `Format: +${callingCode} [number]`,
        digitsCount: nationalNumber.length,
        requiredDigits: requiredDigits,
        country: detectedCountry,
        formattedNumber: parsedNumber.formatInternational()
      };
    } else {
      // Number is not valid - provide specific feedback
      const currentDigits = nationalNumber.length;
      
      // Check if number is too short or too long using example number (works for ALL countries)
      let expectedLength = requiredDigits;
      
      // Try to get expected length from example if not already set (works for all countries)
      if (!expectedLength && detectedCountry) {
        try {
          if (examples[detectedCountry]) {
            const exampleNumber = parsePhoneNumber(examples[detectedCountry], detectedCountry);
            if (exampleNumber && exampleNumber.nationalNumber) {
              expectedLength = exampleNumber.nationalNumber.length;
            }
          }
        } catch (e) {
          // Fallback to typical lengths if example not available
          const typicalLengths = {
            'US': 10, 'CA': 10, 'IN': 10, 'GB': 10, 'AU': 9, 'DE': 11, 'FR': 9,
            'IT': 9, 'ES': 9, 'BR': 10, 'MX': 10, 'JP': 10, 'CN': 11, 'KR': 10,
            'AE': 9, 'SA': 9, 'SG': 8, 'MY': 9, 'TH': 9, 'ID': 9, 'PH': 10,
            'VN': 9, 'NZ': 8, 'ZA': 9, 'PK': 10, 'BD': 10, 'RU': 10, 'TR': 10
          };
          expectedLength = detectedCountry && typicalLengths[detectedCountry] ? typicalLengths[detectedCountry] : null;
        }
      }
      
      if (expectedLength) {
        if (currentDigits < expectedLength) {
          return {
            isValid: false,
            isValidating: true,
            message: `Phone number is too short for ${countryName || 'this country'}`,
            formatHint: detailedHint || formatHint || `Expected ${expectedLength} digits after country code +${callingCode}`,
            digitsCount: currentDigits,
            requiredDigits: expectedLength,
            country: detectedCountry,
            formattedNumber: null
          };
        } else if (currentDigits > expectedLength + 2) {
          return {
            isValid: false,
            isValidating: true,
            message: `Phone number is too long for ${countryName || 'this country'}`,
            formatHint: detailedHint || formatHint || `Expected around ${expectedLength} digits after country code +${callingCode}`,
            digitsCount: currentDigits,
            requiredDigits: expectedLength,
            country: detectedCountry,
            formattedNumber: null
          };
        }
      }

      // Provide specific format error if available
      let errorMessage = formatError || `Invalid ${countryName ? countryName + ' ' : ''}phone number`;
      let errorHint = detailedHint || formatHint || `Please check the number format for country code +${callingCode}`;
      
      // Always try to get example format for better user guidance (works for ALL countries)
      // Use the example we already fetched earlier for consistency
      if (!errorHint.includes('Example:') && exampleNumber && exampleNational) {
        const formattedExample = exampleNumber.formatInternational();
        errorHint = `Example: ${formattedExample}`;
        
        // Add prefix information for ALL countries if available
        if (examplePrefix) {
          const userPrefix = nationalNumber.length >= examplePrefix.length 
            ? nationalNumber.substring(0, examplePrefix.length)
            : nationalNumber.substring(0, Math.min(3, nationalNumber.length));
          
          if (userPrefix !== examplePrefix && nationalNumber.length >= 2) {
            // User's prefix doesn't match - update message
            errorMessage = `Invalid format for ${countryName || 'this country'}. Numbers typically start with ${examplePrefix}`;
            errorHint = `Example: ${formattedExample} (starts with ${examplePrefix}${exampleNational.length > examplePrefix.length ? '...' : ''})`;
          } else {
            // Add prefix hint even if it matches for clarity
            errorHint = `Example: ${formattedExample} (starts with ${examplePrefix})`;
          }
        }
      } else if (detectedCountry && !errorHint.includes('Example:')) {
        // Fallback: Try to fetch example if not already fetched
        try {
          if (examples[detectedCountry]) {
            const fallbackExample = parsePhoneNumber(examples[detectedCountry], detectedCountry);
            if (fallbackExample) {
              errorHint = `Example: ${fallbackExample.formatInternational()}`;
              const fallbackNational = fallbackExample.nationalNumber;
              if (fallbackNational) {
                const fallbackPrefix = fallbackNational.substring(0, Math.min(3, fallbackNational.length));
                if (fallbackPrefix) {
                  errorHint += ` (starts with ${fallbackPrefix})`;
                }
              }
            }
          }
        } catch (e) {
          // Keep existing hint if example fetch fails
        }
      }
      
      return {
        isValid: false,
        isValidating: true,
        message: errorMessage,
        formatHint: errorHint,
        digitsCount: currentDigits,
        requiredDigits: requiredDigits,
        country: detectedCountry,
        formattedNumber: null
      };
    }
  } catch (error) {
    // Parse error - provide helpful feedback
    const digitsOnly = trimmed.replace(/\D/g, '');
    const countryCodeMatch = trimmed.match(/^\+(1\d{0,2}|\d{1,3})/);
    
    if (!countryCodeMatch) {
      return {
        isValid: false,
        isValidating: true,
        message: 'Invalid country code',
        formatHint: 'Country code must be 1-3 digits (e.g., +1 for US, +91 for India, +971 for UAE)',
        digitsCount: digitsOnly.length,
        requiredDigits: null,
        country: null
      };
    }

    return {
      isValid: false,
      isValidating: true,
      message: '',
      formatHint: '',
      digitsCount: digitsOnly.length,
      requiredDigits: null,
      country: null
    };
  }
};

/**
 * Get country name from ISO code
 * @param {string} countryCode - ISO country code
 * @returns {string} - Country name
 */
const getCountryName = (countryCode) => {
  const countryNames = {
    'IN': 'India',
    'US': 'United States',
    'GB': 'United Kingdom',
    'CA': 'Canada',
    'AU': 'Australia',
    'DE': 'Germany',
    'FR': 'France',
    'IT': 'Italy',
    'ES': 'Spain',
    'BR': 'Brazil',
    'MX': 'Mexico',
    'JP': 'Japan',
    'CN': 'China',
    'KR': 'South Korea',
    'AE': 'United Arab Emirates',
    'SA': 'Saudi Arabia',
    'SG': 'Singapore',
    'MY': 'Malaysia',
    'TH': 'Thailand',
    'ID': 'Indonesia',
    'PH': 'Philippines',
    'VN': 'Vietnam',
    'NZ': 'New Zealand',
    'ZA': 'South Africa',
    'EG': 'Egypt',
    'NG': 'Nigeria',
    'KE': 'Kenya',
    'PK': 'Pakistan',
    'BD': 'Bangladesh',
    'LK': 'Sri Lanka',
    'NP': 'Nepal',
    'RU': 'Russia',
    'TR': 'Turkey',
    'PL': 'Poland',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'CH': 'Switzerland',
    'AT': 'Austria',
    'SE': 'Sweden',
    'NO': 'Norway',
    'DK': 'Denmark',
    'FI': 'Finland',
    'GR': 'Greece',
    'PT': 'Portugal',
    'IE': 'Ireland',
    'IL': 'Israel',
    'AR': 'Argentina',
    'CL': 'Chile',
    'CO': 'Colombia',
    'PE': 'Peru',
    'VE': 'Venezuela',
    'EC': 'Ecuador',
    'UY': 'Uruguay',
    'PY': 'Paraguay',
    'BO': 'Bolivia',
    'CR': 'Costa Rica',
    'PA': 'Panama',
    'GT': 'Guatemala',
    'HN': 'Honduras',
    'NI': 'Nicaragua',
    'SV': 'El Salvador',
    'DO': 'Dominican Republic',
    'CU': 'Cuba',
    'JM': 'Jamaica',
    'TT': 'Trinidad and Tobago',
    'BB': 'Barbados',
    'BS': 'Bahamas',
    'BZ': 'Belize',
    'GY': 'Guyana',
    'SR': 'Suriname',
    'GF': 'French Guiana',
    'FK': 'Falkland Islands',
    'GS': 'South Georgia',
    'AQ': 'Antarctica',
    'TF': 'French Southern Territories',
    'HM': 'Heard Island',
    'BV': 'Bouvet Island',
    'IO': 'British Indian Ocean Territory',
    'CX': 'Christmas Island',
    'CC': 'Cocos Islands',
    'NF': 'Norfolk Island',
    'PN': 'Pitcairn',
    'SH': 'Saint Helena',
    'AC': 'Ascension Island',
    'TA': 'Tristan da Cunha',
    'GS': 'South Georgia',
    'PM': 'Saint Pierre and Miquelon',
    'WF': 'Wallis and Futuna',
    'PF': 'French Polynesia',
    'NC': 'New Caledonia',
    'VU': 'Vanuatu',
    'FJ': 'Fiji',
    'PG': 'Papua New Guinea',
    'SB': 'Solomon Islands',
    'TO': 'Tonga',
    'TV': 'Tuvalu',
    'KI': 'Kiribati',
    'NR': 'Nauru',
    'PW': 'Palau',
    'FM': 'Micronesia',
    'MH': 'Marshall Islands',
    'MP': 'Northern Mariana Islands',
    'GU': 'Guam',
    'AS': 'American Samoa',
    'WS': 'Samoa',
    'CK': 'Cook Islands',
    'NU': 'Niue',
    'TK': 'Tokelau',
    'PW': 'Palau',
    'MH': 'Marshall Islands',
    'FM': 'Micronesia',
    'MP': 'Northern Mariana Islands',
    'GU': 'Guam',
    'AS': 'American Samoa',
    'PR': 'Puerto Rico',
    'VI': 'US Virgin Islands',
    'VG': 'British Virgin Islands',
    'AI': 'Anguilla',
    'AG': 'Antigua and Barbuda',
    'AW': 'Aruba',
    'BS': 'Bahamas',
    'BB': 'Barbados',
    'BZ': 'Belize',
    'BM': 'Bermuda',
    'VG': 'British Virgin Islands',
    'KY': 'Cayman Islands',
    'DM': 'Dominica',
    'DO': 'Dominican Republic',
    'GD': 'Grenada',
    'GP': 'Guadeloupe',
    'HT': 'Haiti',
    'JM': 'Jamaica',
    'MQ': 'Martinique',
    'MS': 'Montserrat',
    'KN': 'Saint Kitts and Nevis',
    'LC': 'Saint Lucia',
    'VC': 'Saint Vincent and the Grenadines',
    'SX': 'Sint Maarten',
    'TT': 'Trinidad and Tobago',
    'TC': 'Turks and Caicos',
    'VI': 'US Virgin Islands',
    'AD': 'Andorra',
    'AL': 'Albania',
    'AM': 'Armenia',
    'AT': 'Austria',
    'AZ': 'Azerbaijan',
    'BY': 'Belarus',
    'BE': 'Belgium',
    'BA': 'Bosnia and Herzegovina',
    'BG': 'Bulgaria',
    'HR': 'Croatia',
    'CY': 'Cyprus',
    'CZ': 'Czech Republic',
    'DK': 'Denmark',
    'EE': 'Estonia',
    'FO': 'Faroe Islands',
    'FI': 'Finland',
    'FR': 'France',
    'GE': 'Georgia',
    'DE': 'Germany',
    'GI': 'Gibraltar',
    'GR': 'Greece',
    'GL': 'Greenland',
    'GG': 'Guernsey',
    'HU': 'Hungary',
    'IS': 'Iceland',
    'IE': 'Ireland',
    'IM': 'Isle of Man',
    'IT': 'Italy',
    'JE': 'Jersey',
    'LV': 'Latvia',
    'LI': 'Liechtenstein',
    'LT': 'Lithuania',
    'LU': 'Luxembourg',
    'MK': 'North Macedonia',
    'MT': 'Malta',
    'MD': 'Moldova',
    'MC': 'Monaco',
    'ME': 'Montenegro',
    'NL': 'Netherlands',
    'NO': 'Norway',
    'PL': 'Poland',
    'PT': 'Portugal',
    'RO': 'Romania',
    'RU': 'Russia',
    'SM': 'San Marino',
    'RS': 'Serbia',
    'SK': 'Slovakia',
    'SI': 'Slovenia',
    'ES': 'Spain',
    'SJ': 'Svalbard and Jan Mayen',
    'SE': 'Sweden',
    'CH': 'Switzerland',
    'UA': 'Ukraine',
    'GB': 'United Kingdom',
    'VA': 'Vatican City',
    'AX': 'Åland Islands',
    'XK': 'Kosovo'
  };
  
  return countryNames[countryCode] || countryCode;
};

/**
 * Format phone number as user types (AsYouType)
 * @param {string} phoneNumber - Phone number
 * @param {string} countryCode - ISO country code
 * @returns {string} - Formatted phone number
 */
export const formatPhoneAsYouType = (phoneNumber, countryCode = null) => {
  if (!phoneNumber) return '';
  
  try {
    const formatter = new AsYouType(countryCode);
    return formatter.input(phoneNumber);
  } catch (error) {
    return phoneNumber;
  }
};

/**
 * Get all available countries with their calling codes
 * @returns {Array} - Array of {code, name, callingCode}
 */
export const getAvailableCountries = () => {
  const countries = getCountries();
  return countries.map(code => {
    try {
      const callingCode = getCountryCallingCode(code);
      return {
        code: code,
        name: getCountryName(code),
        callingCode: `+${callingCode}`
      };
    } catch (error) {
      return null;
    }
  }).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));
};

